/* eslint-disable no-undef */
/* eslint-disable no-else-return */

import set from 'lodash/set';

import {
  ASSIGN_USER,
  LOAD_CHAT_MESSAGES,
  SET_CASE_SELECTOR,
  ACTIVE_JOB,
  SET_ACTIVE_CHAT,
  OPEN_DASHBOARD_VIEW,
  CHAT_SEND_FILE_STARTED,
  CHAT_SEND_FILE_COMPLETED,
  CHAT_SEND_FILE_ERROR,
  CHAT_SEND_FILE_CLEAR,
  UPDATED_USER,
  DEAUTH_USER,
  WELCOME_EMAIL_REQUEST_CLEAN,
  WELCOME_EMAIL_IS_SENT,
  RESEND_WELCOME_EMAIL_FOR_JOB_ERROR,
  UPDATE_JOB,
  LOAD_CHAT_PARTICIPANTS,
  LOAD_JOBS,
  PUT_JOB,
  SHOW_SEND_FILE_MODAL,
  REQUEST_PUT_JOB_SUCCESS,
  INLINE_UPDATE_JOB_NEEDS_ATTENTION_MESSAGE_COUNT,
  INLINE_UPDATE_JOB_QH_DISMISSALS,
  PUT_MESSAGE_NEEDS_ACTION_RESPONSE,
  UNREDACTED_MESSAGE,
  CLEAR_UNREDACTED_MESSAGE,
  ENDUSER_JOINED_CHAT,
  LOAD_ALL_CHAT_PARTICIPANTS,
  READ_NOTE_MENTION,
  ADD_REPLY,
  DELETE_REPLY,
  PATCH_JOB,
  INLINE_UPDATE_JOB_NEEDS_ACTION_MESSAGE_COUNT,
  UPDATE_JOB_SEARCH,
  UPDATE_JOB_IDS_INBOX_FILTER,
  SET_EXPAND_SEARCH_BAR,
  UPDATE_SEARCH_RESULTS_COUNT,
  UPDATE_TOTAL_CHAT_COUNT,
  UPDATE_VIRUS_SCAN_STATUS,
  NEW_MESSAGE,
  UPDATE_LAST_VIEWED_MESSAGE,
  SET_ACTIVE_CHAT_MEDIA,
} from '../constants/actions';
import { saveLastActiveChat } from '../helpers/localStorage';
import User from '../models/user';
import completeReleaseIssues from '../actions/job-ts';
import * as commonReducer from './common';
import {
  buildIdList,
  isJobPrivacy,
  isVisibleToOperator,
  incrementJobNeedsAttention,
  incrementJobNeedsAction,
  resolveJobId,
} from './helpers/index';

export const initialState = {
  ...commonReducer.initialState,
  activeJob: {},
  activeChat: {},
  caseSelectorId: 'assigned',
  chatIdsByJobId: {},
  chats: {},
  caseTypes: null,

  lastJobRequest: SERVER_TIMESTAMP_FROM_POLL,
  uploadFileError: '',
  uploadFileIsInProgress: false,
  welcomeEmailError: '',
  welcomeEmailRequest: false,
  presetFilterIds: [],
  showSendFileModal: false,
  fileToSend: null,
  markChatUnreadError: '',
  search: null,
  jobIds: null,
  expandInboxSearchBar: false,
};

const sortJobsByDate = (jobs) =>
  jobs.sort((firstEle, secondEle) => {
    const d1 = new Date(firstEle?.createdAt);
    const d2 = new Date(secondEle?.createdAt);
    if (d1 > d2) return -1;
    if (d2 > d1) return 1;
    return 0;
  });

// A case PATCH echoes the pre-update updatedAt, so an equal one must still apply.
const isStaleJobUpdate = (incoming, known) => {
  if (!incoming?.updatedAt || !known?.updatedAt) return false;
  return (
    new Date(incoming.updatedAt).getTime() < new Date(known.updatedAt).getTime()
  );
};

export const updateJobHelper = (action, state) => {
  const { operatorId, job, permissions } = action.payload;
  const activeJob = { ...state.activeJob };
  let list = state.list.slice();
  const { filter } = state;
  const changesForMatchedActiveJob = {};
  let changesForUpdatedList = {};
  // this is brittle and relies on only one filter being set based on the view
  const searchFilter = filter?.chats || filter?.case;
  const isJobInFilteredList = searchFilter
    ? commonReducer.matchesSearchFilter(job, searchFilter)
    : true;
  const isActiveJob = job.id === activeJob.id;
  // No need to update
  if (!isActiveJob && !isJobInFilteredList) return state;

  // Update list of jobs
  if (isJobInFilteredList) {
    list = list.map((item) => {
      // Skip on no match
      if (job.id !== item.id) return item;

      // If we should redact, override item with redacted job
      if (
        isJobPrivacy(job, 'private-redacted') &&
        !isVisibleToOperator(job, operatorId, permissions)
      ) {
        return {
          id: job.id,
          displayId: job.displayId,
          createdAt: job.createdAt,
          assignedOperator: job.assignedOperator,
          privacy: job.privacy,
          isOpen: job.isOpen,
          isClosed: job.isClosed,
          latestActivity: job.latestActivity,
        };
      }

      // Match found and do extra modifying to job before injecting into list
      if (job?.lastMessage?.authorId === operatorId)
        job.hasUnreadMessages = false;

      // this is a hack to prevent invalid dates getting set.  needs further debug
      if (!job?.createdAt) job.createdAt = item.createdAt;
      return job;
    });
    if (!list.includes(job)) list.push(job);
    list = sortJobsByDate(list); // Sort the list after updating/adding the job
    changesForUpdatedList = { list, ...buildIdList(list) };
  }

  // Update active job
  if (isActiveJob) {
    if (
      isJobPrivacy(job, 'private-redacted') &&
      !isVisibleToOperator(job, operatorId, permissions)
    ) {
      changesForMatchedActiveJob.activeJob = {};
      changesForMatchedActiveJob.activeChat = {};
    } else if (!isStaleJobUpdate(job, activeJob)) {
      changesForMatchedActiveJob.activeJob = { ...activeJob, ...job };
    }
  }

  return {
    ...changesForUpdatedList,
    ...changesForMatchedActiveJob,
  };
};

export const LOOK_CASE_IDS_HASH = {
  171: 'openCases',
  172: 'waitingOnOptIn',
  174: 'unansweredOptIns',
  405: 'responseRecommended',
};

// eslint-disable-next-line consistent-return
const jobs = (state = initialState, action) => {
  switch (action.type) {
    case NEW_MESSAGE: {
      const { list } = state;
      const changes = {
        ...buildIdList(list),
        ...updateJobHelper(action, state),
      };

      // Add media to activeChat.media array if this is a media message
      const { message } = action.payload;

      // A sent scheduled message is removed via the precise SCHEDULED_MESSAGE_CANCELLED
      // live update sendScheduledMessage emits — not by clearing the whole array
      // here, which used to wipe the operator's other still-pending messages.

      if (message && message.fileUrl && message.isFile) {
        const activeChat = { ...state.activeChat };
        const existingMedia = activeChat.media || [];

        // Check if media already exists to avoid duplicates
        const mediaExists = existingMedia.some(
          (media) => media.fileUrl === message.fileUrl,
        );

        if (!mediaExists) {
          const newMediaItem = {
            _id: message.id || message._id,
            chatId: message.chatId,
            authorId: message.author?.id || message.authorId,
            isAI: message.isAI || false,
            isEvent: message.isEvent,
            isInbound: message.isInbound,
            body: message.messageBody || message.body || '',
            needsAttention: message.needsAttention,
            isFile: message.isFile,
            type: message.type || 'media',
            isImage:
              message.isImage ||
              message.mediaInfo?.contentType?.startsWith('image/') ||
              false,
            fileUrl: message.fileUrl,
            messageGroup: message.messageGroup,
            providerId: message.providerId,
            mediaInfo: message.mediaInfo || {},
            deliveryStatus: message.deliveryStatus,
            channel: message.channel,
            createdAt: message.createdAt,
            isImageRedacted: message.isImageRedacted || false,
            redactionViewedEvents: message.redactionViewedEvents || [],
            virusScanStatus: message.virusScanStatus,
          };
          activeChat.media = [...existingMedia, newMediaItem];
          changes.activeChat = activeChat;
        }
      }

      return {
        ...state,
        ...changes,
      };
    }
    case completeReleaseIssues?.fulfilled?.type: {
      // update job with updated release issues
      const { releaseIssues, eventLog } = action.payload;
      const { list, activeJob } = state;
      return {
        ...state,
        activeJob: {
          // update active job
          ...activeJob,
          releaseIssues,
          eventLog,
        }, // update list of jobs
        list: list.map((job) =>
          job.id === activeJob.id ? { ...job, releaseIssues, eventLog } : job,
        ),
      };
    }
    case 'GET_SCHEDULED_MESSAGES': {
      return { ...state, scheduledMessages: action.payload };
    }
    case READ_NOTE_MENTION: {
      const { payload } = action;
      const { userId, noteId, replyId } = payload;
      const activeNotes = { ...state.activeNotes };
      const activeJob = { ...state.activeJob };
      const theNote = activeNotes?.notes?.find((note) => note?._id === noteId);

      const unreadNoteMentions = { ...activeJob.unreadNoteMentions };
      delete unreadNoteMentions[userId];
      const newActiveJob = { ...activeJob, unreadNoteMentions };
      const list = state.list.map((job) => {
        if (job._id === activeJob?.id) {
          set(job, 'unreadNoteMentions', unreadNoteMentions);
        }
        return job;
      });

      if (replyId) {
        // remove unread state from the reply
        const theReply = theNote?.replies?.find(
          (reply) => reply?._id === replyId,
        );
        const notReadMentionedUsers = { ...theReply?.notReadMentionedUsers };
        delete notReadMentionedUsers[userId];
        theReply.notReadMentionedUsers = notReadMentionedUsers;
        theNote?.replies?.map((reply) =>
          reply?._id === replyId ? theReply : reply,
        );
        const updatedNotes = activeNotes?.notes?.map((note) =>
          note?._id === noteId ? theNote : note,
        );
        return {
          ...state,
          activeNotes: {
            ...state.activeNotes,
            notes: updatedNotes,
          },
          activeJob: newActiveJob,
          list,
        };
      } else {
        // remove unread state from the note
        const notReadMentionedUsers = { ...theNote?.notReadMentionedUsers };
        delete notReadMentionedUsers[userId];
        return {
          ...state,
          activeNotes: {
            ...state.activeNotes,
            notes: [
              ...state.activeNotes.notes.map((note) =>
                note?._id === noteId
                  ? { ...note, notReadMentionedUsers }
                  : note,
              ),
            ],
          },
          activeJob: newActiveJob,
          list,
        };
      }
    }
    case 'CANCEL_SCHEDULED_MESSAGE': {
      const { payload } = action;
      const { id } = payload;
      const scheduledMessages = (state.scheduledMessages || []).filter(
        (m) => m.id !== id,
      );
      return { ...state, scheduledMessages };
    }
    case 'SCHEDULED_MESSAGE_CREATED': {
      const { scheduledMessage, chatId, triggerType } = action.payload;
      // Quiet-hours-deferred sends ride the same event channel but don't
      // belong in this Redux slice — they live in the QH-tasks React Query
      // cache and the event-processor invalidates it independently.
      if (triggerType === 'quiet-hours') return state;
      // Only add if it's for the current active chat
      if (state.activeJob?.primaryChatId === chatId) {
        const scheduledMessages = [...(state.scheduledMessages || [])];
        // Avoid duplicates
        if (!scheduledMessages.some((m) => m.id === scheduledMessage.id)) {
          scheduledMessages.push(scheduledMessage);
        }
        return { ...state, scheduledMessages };
      }
      return state;
    }
    case 'SCHEDULED_MESSAGE_CANCELLED': {
      const { scheduledMessage, triggerType } = action.payload;
      if (triggerType === 'quiet-hours') return state;
      const scheduledMessages = (state.scheduledMessages || []).filter(
        (m) => m.id !== scheduledMessage.id,
      );
      return { ...state, scheduledMessages };
    }
    case 'BULK_UPDATE_JOB': {
      return { ...state };
    }
    case 'MODIFIED_JOB_LANGUAGE': {
      const { inTranslationMode, jobId, languagePreference } = action.payload;
      const activeJob = { ...state.activeJob };
      if (activeJob.id === jobId) {
        set(activeJob, 'inTranslationMode', inTranslationMode);
        set(activeJob, 'languagePreference', languagePreference);
      }
      // find the job and also update (duplicate references are fun!)
      const list = state.list.map((job) => {
        if (job._id === jobId) {
          set(job, 'inTranslationMode', inTranslationMode);
          set(job, 'languagePreference', languagePreference);
        }
        return job;
      });

      return { ...state, activeJob, list };
    }
    case 'FILTER_ITEMS': {
      const { filter, type } = action.payload;
      const changes = {
        filter: { ...state.filter },
      };
      changes.filter[type] = filter;
      changes[`${type}Filter`] = filter;
      return { ...state, ...changes };
    }
    case 'MODIFIED_BRANDING': {
      // TODO: is this reducer doing anything nothing is getting returned
      const { branding, jobId } = action.payload;
      const activeJob = { ...state.activeJob };
      if (activeJob.id === jobId) {
        set(activeJob, 'branding', branding);
      }
      // TODO: remove once reducer actually returns something
      break;
    }

    case OPEN_DASHBOARD_VIEW: {
      return { ...state, form: {} };
    }
    case LOAD_CHAT_PARTICIPANTS: {
      let changes;
      if (action.payload.users) {
        const { chatId } = action.payload;
        const chats = { ...state.chats };
        if (!chats[chatId]) chats[chatId] = {};
        chats[chatId].participants = action.payload.users;
        changes = { chats };
      }
      return { ...state, ...changes };
    }
    case LOAD_ALL_CHAT_PARTICIPANTS: {
      const { chatId, activeParticipants } = action.payload;
      if (chatId && activeParticipants) {
        const chats = {
          ...state.chats,
          [chatId]: {
            ...(state.chats[chatId] || {}),
            participants: activeParticipants,
          },
        };
        return { ...state, chats };
      }
      return state;
    }
    case LOAD_CHAT_MESSAGES: {
      let changes;
      if (action.payload.chat) {
        const { chat } = action.payload;
        const chats = { ...state.chats };
        if (chats[chat.id]) {
          chats[chat.id] = chat;
        }
        changes = { chats };
      }
      return { ...state, ...changes };
    }
    case UNREDACTED_MESSAGE: {
      const activeChat = { ...state.activeChat };
      activeChat.unredactedMessage = action.payload;
      return { ...state, activeChat };
    }
    case CLEAR_UNREDACTED_MESSAGE: {
      const activeChat = { ...state.activeChat };
      activeChat.unredactedMessage = {};
      return { ...state, activeChat };
    }
    case UPDATE_JOB: {
      return { ...state, ...updateJobHelper(action, state) };
    }
    case INLINE_UPDATE_JOB_QH_DISMISSALS: {
      // Surgical: only touches the QH-dismissals field. Avoids UPDATE_JOB's
      // partial-Job wrap + list resort that flicker the inbox sidebar.
      const { jobId, quietHoursWarningDismissals } = action.payload;
      if (!jobId) return state;
      const isActiveJob = resolveJobId(state.activeJob) === jobId;
      const newList = (state.list || []).map((item) =>
        resolveJobId(item) === jobId
          ? { ...item, quietHoursWarningDismissals }
          : item,
      );
      return {
        ...state,
        ...(isActiveJob && {
          activeJob: { ...state.activeJob, quietHoursWarningDismissals },
        }),
        list: newList,
      };
    }
    case PATCH_JOB:
    case PUT_JOB: {
      const job = action?.payload ?? {};
      const list = [...(state?.list ?? [])];
      const changes = {};
      if (job.id) {
        const newList = list.map((j) => {
          if (j.id === job.id) {
            return job;
          }
          return j;
        });
        changes.list = newList;
        changes.listById = buildIdList(newList).listById;
      }
      return {
        ...state,
        ...changes,
      };
    }
    case INLINE_UPDATE_JOB_NEEDS_ATTENTION_MESSAGE_COUNT: {
      const newChanges = {
        activeJob: state.activeJob,
        list: state.list,
      };
      const message = action.payload?.message;
      if (state.activeJob.primaryChatId === message.chatId) {
        newChanges.activeJob = incrementJobNeedsAttention(
          state.activeJob,
          action.payload.needsAttentionMessageCount,
        );
      }

      newChanges.list = state.list.map((singleJob) => {
        if (message.chatId === singleJob.primaryChatId) {
          return incrementJobNeedsAttention(
            singleJob,
            action.payload.needsAttentionMessageCount,
          );
        }
        return { ...singleJob };
      });

      return { ...state, ...newChanges };
    }

    case INLINE_UPDATE_JOB_NEEDS_ACTION_MESSAGE_COUNT: {
      const { job } = action.payload;
      const newChanges = {
        activeJob: { ...state.activeJob },
        list: [...state.list],
      };

      const message = action.payload.message || action.payload.msg;
      if (state.activeJob.primaryChatId === message.chatId) {
        newChanges.activeJob = incrementJobNeedsAction(
          newChanges.activeJob,
          job.needsAction?.hasQuestionMessageCount,
          'hasQuestionMessageCount',
        );
      }

      newChanges.list = newChanges.list.map((singleJob) => {
        if (message.chatId === singleJob.primaryChatId) {
          return incrementJobNeedsAction(
            { ...singleJob },
            job.needsAction?.hasQuestionMessageCount,
            'hasQuestionMessageCount',
          );
        }
        return singleJob;
      });

      return { ...state, ...newChanges };
    }
    case PUT_MESSAGE_NEEDS_ACTION_RESPONSE: {
      const listOfJobs = state?.list ?? [];
      const updatedJobId = action?.payload?.body?.message?.job?.id ?? {};
      const jobHasQuestionCount =
        action?.payload?.body?.message?.job?.needsAction
          ?.hasQuestionMessageCount ?? 0;
      const { activeChat } = state;
      if (state.activeJob.primaryChatId !== action.payload.body.message.chatId)
        return state;

      // Map over the list of jobs and update the job that matches the ID of the updated job
      const updatedListOfJobs = listOfJobs.map((job) =>
        job._id === updatedJobId
          ? {
              ...job,
              needsAction: { hasQuestionMessageCount: jobHasQuestionCount },
            }
          : job,
      );

      return {
        ...state,
        list: updatedListOfJobs,
        activeChat: { ...activeChat },
      };
    }

    case ASSIGN_USER: {
      let activeJob;
      const { _id, operatorId } = action.payload;
      const changes = {};

      if (state?.activeJob?.id === _id) {
        activeJob = state.activeJob;
        activeJob.operatorIds =
          operatorId && operatorId !== 'unassigned' ? [operatorId] : [];
        changes.activeJob = activeJob;
      }
      return { ...state, ...changes };
    }
    case LOAD_JOBS: {
      let changes;
      let { list } = action.payload?.items ?? [];
      const { operatorId, timestamp } = action.payload?.items ?? [];

      if (list && timestamp > state.lastJobRequest) {
        list = sortJobsByDate(list);
        changes = {
          list,
          ...buildIdList(list),
        };

        if (state?.activeJob?.id === _id) {
          activeJob = state.activeJob;
          activeJob.operatorIds =
            operatorId && operatorId !== 'unassigned' ? [operatorId] : [];
          changes.activeJob = activeJob;
        }

        return { ...state, ...changes };
      }
      break;
    }
    case 'REQUEST_LOAD_NOTES_INIT': {
      const activeNotes = {
        ...state.activeNotes,
        isLoading: true,
        isLoaded: false,
      };

      return { ...state, activeNotes };
    }
    case 'REQUEST_LOAD_NOTES_SUCCESS': {
      const activeNotes = {
        ...state.activeNotes,
        isLoading: false,
        isLoaded: true,
      };

      return { ...state, activeNotes };
    }
    case 'LOAD_NOTES': {
      const activeNotes = { ...state.activeNotes };
      activeNotes.notes = action.payload;
      return { ...state, activeNotes };
    }
    case 'DELETE_NOTE': {
      const id = action.payload?._id;
      const activeNotes = { ...state.activeNotes };
      activeNotes.notes = [...activeNotes.notes].map((n) =>
        n._id === id ? action.payload : n,
      );
      return { ...state, activeNotes };
    }
    case 'CREATE_NOTE': {
      if (action?.payload?.caseId === state?.activeJob?.id) {
        const activeNotes = { ...state.activeNotes };
        const alreadyExists = !!activeNotes.notes.find(
          (n) => n._id === action.payload?._id,
        );
        if (!alreadyExists)
          activeNotes.notes = [...activeNotes.notes, action.payload];
        return { ...state, activeNotes };
      }
      return state;
    }
    case DELETE_REPLY:
    case ADD_REPLY: {
      const id = action.payload?._id;
      const replies = action.payload?.replies;
      return {
        ...state,
        activeNotes: {
          ...state.activeNotes,
          notes: state.activeNotes.notes.map((note) =>
            note?._id === id ? { ...note, replies } : note,
          ),
        },
      };
    }
    case SET_CASE_SELECTOR: {
      return { ...state, caseSelectorId: action.payload };
    }
    case 'CLEAR_ACTIVE_JOB': {
      const caseId = action?.payload;
      let list = [...state.list];
      if (caseId) {
        list = list.filter((c) => c._id !== caseId);
      }
      return {
        ...state,
        list,
        activeJob: {},
        activeChat: {},
        previousActiveChat: state?.activeChat?.id,
      };
    }
    case ACTIVE_JOB: {
      const activeJob = action.payload || {};
      return { ...state, activeJob };
    }
    case SET_ACTIVE_CHAT: {
      const activeJob = state.activeJob || {};
      let chat = action?.payload?.chat;
      const { operatorId, permissions } = action.payload;
      const isJobPrivate = isJobPrivacy(activeJob, 'private');

      if (isJobPrivate) {
        const visibleToLoggedInOp = isVisibleToOperator(
          activeJob,
          operatorId,
          permissions,
        );
        if (!visibleToLoggedInOp) {
          chat = { noAccess: true };
        }
      }
      saveLastActiveChat(action.payload.jobId);
      return {
        ...state,
        activeChat: {
          activeJob,
          ...chat,
          isLastMessageInLog: true,
        },
      };
    }
    case CHAT_SEND_FILE_STARTED: {
      return { ...state, uploadFileIsInProgress: true, uploadFileError: '' };
    }
    case CHAT_SEND_FILE_CLEAR:
    case CHAT_SEND_FILE_COMPLETED: {
      const url = action.payload;
      return {
        ...state,
        uploadFileUrl: url,
        uploadFileIsInProgress: false,
        uploadFileError: '',
      };
    }
    case CHAT_SEND_FILE_ERROR: {
      return {
        ...state,
        uploadFileIsInProgress: false,
        uploadFileError: action.payload,
      };
    }
    case UPDATED_USER: {
      const user = action.payload;
      const list = state.list.map((job) => {
        if (job.customer && job.customer.id === user.id) {
          return { ...job, customer: user };
        }
        return job;
      });
      const activeChat = state?.activeChat;
      const activeJob = state?.activeJob;
      if (activeChat?.activeJob?.customer) {
        if (activeChat.activeJob.customer.id === user.id) {
          activeChat.activeJob.customer = user;
        }
      }
      if (activeJob?.customer) {
        if (activeJob.customer.id === user.id) {
          activeJob.customer = user;
        }
      }

      return {
        ...state,
        list,
        activeChat,
        activeJob,
        ...buildIdList(list),
      };
    }
    case DEAUTH_USER: {
      return { ...initialState };
    }
    case WELCOME_EMAIL_REQUEST_CLEAN: {
      return { ...state, welcomeEmailRequest: false, welcomeEmailError: '' };
    }
    case WELCOME_EMAIL_IS_SENT: {
      return { ...state, welcomeEmailRequest: true };
    }
    case RESEND_WELCOME_EMAIL_FOR_JOB_ERROR: {
      return { ...state, welcomeEmailError: action.payload };
    }
    case SHOW_SEND_FILE_MODAL: {
      return {
        ...state,
        showSendFileModal: action?.payload?.show ?? false,
        fileToSend: action?.payload?.file ?? null,
      };
    }
    case REQUEST_PUT_JOB_SUCCESS: {
      return {
        ...state,
        requestCompleted: true,
        editObject: {},
        httpRequestError: false,
      };
    }
    case ENDUSER_JOINED_CHAT: {
      const { chat } = action.payload;
      const chats = { ...state?.chats };
      // need to map to expected user format
      const updatedParticipants = chat?.participants?.map(
        (participant) => new User(participant),
      );
      if (chats?.[chat._id]?.participants) {
        chats[chat._id].participants = updatedParticipants;
      }
      return {
        ...state,
        chats,
      };
    }
    case UPDATE_JOB_SEARCH: {
      const update = {};
      if (typeof action?.payload?.search !== 'undefined') {
        update.search = action?.payload?.search;
      }
      if (typeof action?.payload?.searchDisplayValue !== 'undefined') {
        update.searchDisplayValue = action?.payload?.searchDisplayValue;
      }
      return { ...state, ...update };
    }
    case UPDATE_JOB_IDS_INBOX_FILTER: {
      return { ...state, jobIds: action?.payload?.jobIds };
    }
    case SET_EXPAND_SEARCH_BAR: {
      return {
        ...state,
        expandInboxSearchBar: action?.payload?.expandInboxSearchBar,
      };
    }
    case UPDATE_SEARCH_RESULTS_COUNT: {
      return {
        ...state,
        searchResultsCount: action?.payload?.searchResultsCount,
      };
    }
    case UPDATE_TOTAL_CHAT_COUNT: {
      return { ...state, totalChatCount: action?.payload?.totalChatCount };
    }
    case UPDATE_VIRUS_SCAN_STATUS: {
      const { fileUrl, virusScanStatus } = action.payload;
      return {
        ...state,
        activeChat: {
          ...state.activeChat,
          media: state.activeChat?.media?.map((m) => {
            if (m?.fileUrl === fileUrl) {
              return {
                ...m,
                virusScanStatus,
              };
            }
            return m;
          }),
        },
      };
    }
    case UPDATE_LAST_VIEWED_MESSAGE: {
      // The PATCH returns a whole case document; only the read receipt is ours.
      const { body } = action.payload || {};
      const activeJobId = resolveJobId(state.activeJob);
      if (!activeJobId || activeJobId !== resolveJobId(body)) return state;
      if (!body.usersLastViewedMessage) return state;

      return {
        ...state,
        activeJob: {
          ...state.activeJob,
          usersLastViewedMessage: body.usersLastViewedMessage,
        },
      };
    }
    case SET_ACTIVE_CHAT_MEDIA: {
      const { media = [] } = action.payload?.chat || {};
      return { ...state, activeChat: { ...state.activeChat, media } };
    }
    default:
      return state;
  }
};

export default commonReducer.reducer('CASE', jobs);
